<template>
  <div class="bg-white q-pa-md full-height">
    <customer-select
      name="customer"
      v-model="local.customer"
      :hasError="errors.has('customer')"
      :errorLabel="errors.first('customer')"
      :data-vv-as="$tc('customer', 1)"
      v-validate.disable="'required'"
      @input="updateDueDate"
    />

    <customer-address-select
      v-if="local.customer"
      v-model="local.invoiceAddresses"
      :customer="local.customer"
      @reFetchCustomer="handleReFetchCustomer"
      class="q-mt-md"
    />

    <div class="row">
      <q-field
        class="col"
        :error="errors.has('invoiceTitle')"
        :error-label="errors.first('invoiceTitle')"
      >
        <q-input
          name="invoiceTitle"
          :value="local.invoiceTitle"
          :float-label="$t('invoice_title')"
          :data-vv-as="$t('invoice_title')"
          v-validate.disable="'required'"
          @input="val => update('invoiceTitle', val)"
        />
      </q-field>
    </div>

    <div class="row gutter-sm">
      <q-field
        class="col"
        :error="errors.has('invoiceDate')"
        :error-label="errors.first('invoiceDate')"
      >
        <q-datetime
          name="invoiceDate"
          :value="local.invoiceDate"
          type="date"
          :float-label="$t('invoice_date')"
          :data-vv-as="$t('invoice_date')"
          :format="$t('date_format')"
          v-validate.disable="'required'"
          clearable
          @input="val => update('invoiceDate', val)"
        />
      </q-field>

      <q-field
        class="col"
        :error="errors.has('deliveryDate')"
        :error-label="errors.first('deliveryDate')"
      >
        <q-datetime
          name="deliveryDate"
          :value="local.deliveryDate"
          type="date"
          :float-label="$t('delivery_date')"
          :data-vv-as="$t('delivery_date')"
          v-validate.disable="'required'"
          :format="$t('date_format')"
          clearable
          @input="val => update('deliveryDate', val)"
        />
      </q-field>

      <q-field
        class="col"
        :error="errors.has('dueDate')"
        :error-label="errors.first('dueDate')"
      >
        <q-datetime
          name="dueDate"
          :value="local.dueDate"
          type="date"
          :float-label="$t('due_date')"
          :data-vv-as="$t('due_date')"
          v-validate.disable="''"
          :format="$t('date_format')"
          clearable
          @input="val => update('dueDate', val)"
        />
      </q-field>
    </div>

    <div class="row">
       <q-field
        :error="errors.has('invoiceNumber')"
        :error-label="errors.first('invoiceNumber')"
        class="col-6"
      >
        <q-input
          name="invoiceNumber"
          :value="local.invoiceNumber"
          :float-label="$t('invoice_number')"
          :data-vv-as="$t('invoice_number')"
          v-validate.disable="'required'"
          @input="val => update('invoiceNumber', val)"
        />
      </q-field>

       <q-field
        :error="errors.has('status')"
        :error-label="errors.first('status')"
        class="col-3"
      >
        <q-select
          name="status"
          :value="local.status"
          :options="possibleStatus"
          :float-label="$t('invoice_status')"
          :data-vv-as="$t('invoice_status')"
          v-validate.disable="'required'"
          @input="val => update('status', val)"
        />
      </q-field>

      <q-field
        v-if="local.status === 'paid'"
        :error="errors.has('paidAt')"
        :error-label="errors.first('paidAt')"
        class="col-3"
      >
        <q-datetime
          name="paidAt"
          :value="local.paymentDate"
          type="date"
          :float-label="$t('expense_paid_at')"
          :data-vv-as="$t('expense_paid_at')"
          :format="$t('date_format')"
          v-validate.disable="'required'"
          @input="val => update('paymentDate', val)"
        />
      </q-field>
    </div>

    <div class="row">
      <q-field
        class="col-8"
        :error="errors.has('totalNet')"
        :error-label="errors.first('totalNet')"
      >
        <money-input
          name="totalNet"
          type="number"
          :value="local.totalNet"
          :float-label="$t('sum_net')"
          :data-vv-as="$t('sum_net')"
          v-validate.disable="'required|numeric'"
          @input="val => update('totalNet', val)"
        />
      </q-field>
    </div>

    <div class="row">
      <q-field
        class="col-8"
        :error="errors.has('salesTaxAmount')"
        :error-label="errors.first('salesTaxAmount')"
      >
        <money-input
          name="salesTaxAmount"
          type="number"
          :value="local.salesTaxAmount"
          :float-label="$t('sales_tax_amount')"
          :data-vv-as="$t('sales_tax_amount')"
          v-validate.disable="'required|numeric'"
          @input="val => update('salesTaxAmount', val)"
        />
      </q-field>

      <q-field
        class="col-4"
        :error="errors.has('salesTax')"
        :error-label="errors.first('salesTax')"
      >
        <sales-tax-input
          :value="local.salesTaxRate"
          :float-label="$t('sales_tax_rate')"
          use-default
          required
          @input="val => update('salesTaxRate', val)"
        />
      </q-field>
    </div>

    <div class="row">
      <q-field class="col">
        <project-select
          :value="local.projectId"
          :float-label="$tc('project', 1)"
          :data-vv-as="$tc('project', 1)"
          clearable
          @input="val => update('projectId', val)"
        />
      </q-field>
    </div>
  </div>
</template>

<script>
  import CustomerAddressSelect from 'components/Invoices/Create/CustomerAddressSelect.vue'
  import CustomerSelect from 'components/Invoices/Create/CustomerSelect.vue'
  import { CustomersClient } from 'src/api/Customers'
  import MoneyInput from 'components/Common/Inputs/MoneyInput.vue'
  import ProjectSelect from 'components/Common/Inputs/ProjectSelect.vue'
  import SalesTaxInput from 'components/Common/Inputs/SalesTaxInput.vue'
  import { calculateSalesTax } from 'src/utils/calculateSalesTax'
  import { date } from 'quasar'
  import { isNil } from 'lodash'
  import { mapGetters } from 'vuex'

  export default {
    inject: ['$validator'],
    components: {
      CustomerAddressSelect,
      CustomerSelect,
      MoneyInput,
      SalesTaxInput,
      ProjectSelect
    },
    props: {
      value: {
        required: true,
        validator: prop => typeof prop === 'object' || prop === null
      }
    },
    computed: {
      ...mapGetters(['activeTeam']),
      local () {
        return this.value
          ? this.value
          : {
            invoiceTitle: this.$tc('invoice', 1),
            customer: null,
            invoiceAddresses: {
              invoiceAddresses: []
            },
            invoiceDate: null,
            deliveryDate: null,
            dueDate: null,
            invoiceNumber: null,
            totalNet: null,
            salesTaxAmount: null,
            salesTaxRate: null,
            projectId: null,
            status: 'paid'
          }
      },
      invoiceDate () {
        return this.local.invoiceDate
      },
      totalNet () {
        return this.local.totalNet
      },
      salesTaxRate () {
        return this.local.salesTaxRate
      },
      paymentDeadline () {
        return this.customer && this.customer.default_payment_deadline
          ? this.customer.default_payment_deadline
          : this.activeTeam.settings.default_payment_deadline
      },
      possibleStatus () {
        return [{ 'label': this.$t('open'), value: 'open' }, { 'label': this.$t('paid'), value: 'paid' }, { 'label': this.$t('storno'), value: 'storno' }]
      }
    },
    methods: {
      update (key, value) {
        this.$emit('input', { ...this.local, [key]: value })
      },
      updateSalesTaxAmount () {
        if (isNil(this.salesTaxRate) || isNil(this.totalNet)) {
          return
        }

        this.update('salesTaxAmount', calculateSalesTax(this.totalNet, this.salesTaxRate))
      },
      updateDueDate () {
        this.update(
          'salesTaxAmount',
          date.addToDate(this.invoiceDate, { days: this.paymentDeadline })
        )
      },
      handleReFetchCustomer () {
        new CustomersClient()
          .include(['addresses'])
          .get(this.local.customer.id)
          .then(response => {
            this.update('customer', response.data.data[0])
          })

        if (this.errors) {
          this.errors.remove('invoice_address')
        }
      }
    },
    watch: {
      totalNet (val) {
        this.updateSalesTaxAmount()
      },
      salesTaxRate (val) {
        this.updateSalesTaxAmount()
      }
    }
  }
</script>
